import useSelectMenu from 'hooks/useSelectMenu';

import { DEFAULT_PLACEHOLDER, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const Chevron = ({ isOpen }: { isOpen: boolean }) => (
    <span className={`${styles.chevron}${isOpen ? ` ${styles.chevronOpen}` : ''}`}>
        <svg
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    </span>
);

const SelectMenu: FunctionComponent<Props> = ({
    className = '',
    disabled = false,
    error,
    label,
    onChange,
    options,
    placeholder = DEFAULT_PLACEHOLDER,
    required = false,
    testId = TEST_ID,
    value
}) => {
    const {
        containerRef,
        fieldId,
        focusedIndex,
        handleKeyDown,
        handleOptionClick,
        handleToggle,
        isOpen,
        listboxId,
        selectedOption,
        setFocusedIndex
    } = useSelectMenu({ disabled, onChange, options, value });

    const triggerLabel = selectedOption?.label ?? placeholder;
    const isPlaceholder = !selectedOption;

    const triggerClass = `${styles.trigger}${error ? ` ${styles.hasError}` : ''}`;

    return (
        <div
            className={`${styles.selectMenu} ${className}`}
            data-testid={testId}
            ref={containerRef}
        >
            {label ? (
                <label className={styles.label} htmlFor={fieldId}>
                    {label}

                    {required ? <span className={styles.required}>*</span> : null}
                </label>
            ) : null}

            <button
                aria-controls={listboxId}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                className={triggerClass}
                disabled={disabled}
                id={fieldId}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                type="button"
            >
                <span className={isPlaceholder ? styles.placeholder : ''}>{triggerLabel}</span>

                <Chevron isOpen={isOpen} />
            </button>

            {isOpen ? (
                <ul className={styles.list} id={listboxId} role="listbox">
                    {options.map((option, index) => {
                        const isSelected = option.value === value;
                        const isFocused = index === focusedIndex;
                        const optionClass = `${styles.option}${isSelected ? ` ${styles.optionSelected}` : ''}${isFocused ? ` ${styles.optionFocused}` : ''}`;

                        return (
                            <li
                                aria-selected={isSelected}
                                className={optionClass}
                                key={option.value}
                                onClick={() => handleOptionClick(option.value)}
                                onMouseEnter={() => setFocusedIndex(index)}
                                role="option"
                            >
                                {option.label}
                            </li>
                        );
                    })}
                </ul>
            ) : null}

            {error ? <span className={styles.errorMessage}>{error}</span> : null}
        </div>
    );
};

export default SelectMenu;
